/**
 * Step 2: Diagnóstico Patrimonial Inicial
 */
import type { StepProps } from "./types.js";
import { CurrencyInput } from "../CurrencyInput.jsx";
import { RADIUS } from "../theme.js";

interface LoanEntry {
  tipo: string;
  val: number;
  taxa: number;
  descricao: string;
}

const FINANCIAL_ASSETS: [string, string][] = [
  ["ativoLiquidez", "Liquidez / Renda Fixa"],
  ["ativoAcoes", "Ações / Multimercados"],
  ["ativoExterior", "Ativos no Exterior (USD)"],
  ["ativoFundos", "Fundos Privados / FIPs"],
];

const PROPERTY_ASSETS: [string, string][] = [
  ["imoveis", "Imóveis (Residenciais/Comerciais/Rurais)"],
  ["empresas", "Empresas Operacionais (Sócio/Acionista)"],
];

export function Step2Patrimonial({ form, onChange, T }: StepProps) {
  const set = (k: string, v: unknown) => onChange(k, v);
  const setN = (k: string, fld: string, v: unknown) => {
    set(k, { ...(form[k] as Record<string, unknown>), [fld]: v });
  };

  const emprestimos = (form.emprestimos as LoanEntry[] | undefined) || [];

  const addLoan = () => {
    set("emprestimos", [...emprestimos, { tipo: "Empréstimo", val: 0, taxa: 0, descricao: "" }]);
  };

  const updateLoan = (i: number, field: string, value: unknown) => {
    const arr = [...emprestimos];
    arr[i] = { ...arr[i], [field]: value };
    set("emprestimos", arr);
  };

  const removeLoan = (i: number) => {
    set(
      "emprestimos",
      emprestimos.filter((_, j) => j !== i)
    );
  };

  return (
    <>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: "0 0 6px" }}>
        3. Diagnóstico Patrimonial Inicial (Snapshot)
      </h3>
      <p style={{ fontSize: 12, color: T.sub, margin: "0 0 16px" }}>
        Favor preencher com valores estimados para o diagnóstico inicial de eficiência.
      </p>

      {/* Financial Assets */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#fff",
            marginBottom: 10,
            padding: "8px 14px",
            background: T.accent,
            borderRadius: RADIUS.sm,
          }}
        >
          A. Ativos Financeiros (Nacional e Offshore)
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 2fr 1fr",
            gap: 6,
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, padding: "0 4px" }}>
            CLASSE
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, padding: "0 4px" }}>
            INSTITUIÇÃO
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, padding: "0 4px" }}>
            JURISDIÇÃO
          </span>
        </div>
        {FINANCIAL_ASSETS.map(([k, lbl]) => {
          const entry = (form[k] as Record<string, unknown>) || {};
          return (
            <div key={k} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: T.sub, fontWeight: 600, marginBottom: 3 }}>
                {lbl}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr", gap: 6 }}>
                <CurrencyInput
                  value={(entry.val as number) || 0}
                  onChange={(v: number) => setN(k, "val", v)}
                  placeholder="R$ Valor"
                  style={{
                    padding: "7px 10px",
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    color: T.text,
                    fontSize: 12,
                    width: "100%",
                    boxSizing: "border-box" as const,
                  }}
                />
                <input
                  placeholder="Instituição Financeira"
                  value={(entry.inst as string) || ""}
                  onChange={(e) => setN(k, "inst", e.target.value)}
                  style={{
                    padding: "7px 10px",
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    color: T.text,
                    fontSize: 12,
                  }}
                />
                <input
                  placeholder="País"
                  value={(entry.jur as string) || ""}
                  onChange={(e) => setN(k, "jur", e.target.value)}
                  style={{
                    padding: "7px 10px",
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    color: T.text,
                    fontSize: 12,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Real Estate & Investments */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#fff",
            marginBottom: 10,
            padding: "8px 14px",
            background: T.accent,
            borderRadius: RADIUS.sm,
          }}
        >
          B. Imóveis e Participações Societárias
        </div>
        {PROPERTY_ASSETS.map(([k, lbl]) => {
          const entry = (form[k] as Record<string, unknown>) || {};
          return (
            <div key={k} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.sub, fontWeight: 600, marginBottom: 4 }}>
                {lbl}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 80px", gap: 6 }}>
                <CurrencyInput
                  value={(entry.val as number) || 0}
                  onChange={(v: number) => setN(k, "val", v)}
                  placeholder="R$ Valor"
                  style={{
                    padding: "7px 10px",
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    color: T.text,
                    fontSize: 12,
                    width: "100%",
                    boxSizing: "border-box" as const,
                  }}
                />
                <input
                  placeholder="Descrição (localização, empresa...)"
                  value={(entry.desc as string) || ""}
                  onChange={(e) => setN(k, "desc", e.target.value)}
                  style={{
                    padding: "7px 10px",
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    color: T.text,
                    fontSize: 12,
                  }}
                />
                <select
                  value={(entry.titan as string) || "PF"}
                  onChange={(e) => setN(k, "titan", e.target.value)}
                  style={{
                    padding: "7px 10px",
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    color: T.text,
                    fontSize: 12,
                  }}
                >
                  <option value="PF">PF</option>
                  <option value="PJ">PJ</option>
                  <option value="Holding">Holding</option>
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {/* Liabilities */}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#fff",
            marginBottom: 10,
            padding: "8px 14px",
            background: T.accent,
            borderRadius: RADIUS.sm,
          }}
        >
          C. Passivos e Obrigações
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1fr 1.8fr 32px",
            gap: 8,
            marginBottom: 4,
            paddingLeft: 2,
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, textTransform: "uppercase" }}>
            Tipo
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, textTransform: "uppercase" }}>
            Valor (R$)
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, textTransform: "uppercase" }}>
            Taxa % a.a.
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, textTransform: "uppercase" }}>
            Descrição / Banco
          </span>
          <span></span>
        </div>

        {emprestimos.map((emp, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr 1fr 1.8fr 32px",
              gap: 8,
              marginBottom: 8,
              alignItems: "center",
            }}
          >
            <select
              value={emp.tipo || "Empréstimo"}
              onChange={(e) => updateLoan(i, "tipo", e.target.value)}
              style={{
                padding: "7px 10px",
                borderRadius: 7,
                border: `1px solid ${T.border}`,
                background: T.bg,
                color: T.text,
                fontSize: 12,
              }}
            >
              <option>Empréstimo</option>
              <option>Financiamento Imob.</option>
              <option>Financiamento Veículo</option>
              <option>Cartão de Crédito</option>
              <option>Cheque Especial</option>
              <option>Outro</option>
            </select>
            <CurrencyInput
              value={emp.val || 0}
              onChange={(v: number) => updateLoan(i, "val", v)}
              placeholder="0"
              style={{
                padding: "7px 10px",
                borderRadius: 7,
                border: `1px solid ${T.border}`,
                background: T.bg,
                color: T.text,
                fontSize: 12,
                width: "100%",
                boxSizing: "border-box" as const,
              }}
            />
            <input
              type="number"
              placeholder="0,0"
              value={emp.taxa || ""}
              onChange={(e) => updateLoan(i, "taxa", Number(e.target.value))}
              style={{
                padding: "7px 10px",
                borderRadius: 7,
                border: `1px solid ${T.border}`,
                background: T.bg,
                color: T.text,
                fontSize: 12,
              }}
            />
            <input
              placeholder="Banco, finalidade..."
              value={emp.descricao || ""}
              onChange={(e) => updateLoan(i, "descricao", e.target.value)}
              style={{
                padding: "7px 10px",
                borderRadius: 7,
                border: `1px solid ${T.border}`,
                background: T.bg,
                color: T.text,
                fontSize: 12,
              }}
            />
            <button
              onClick={() => removeLoan(i)}
              style={{
                padding: "6px",
                borderRadius: 6,
                background: T.dangerBg,
                border: "none",
                color: T.danger,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        ))}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 4,
          }}
        >
          <button
            onClick={addLoan}
            style={{
              padding: "7px 14px",
              borderRadius: 7,
              border: `1px dashed ${T.border}`,
              background: "transparent",
              color: T.sub,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            + Adicionar Dívida / Financiamento
          </button>
          {emprestimos.some((e) => e.val > 0) && (
            <div style={{ fontSize: 12, color: T.sub }}>
              Total passivos:{" "}
              <strong style={{ color: T.text }}>
                R${" "}
                {emprestimos
                  .reduce((s, e) => s + (Number(e.val) || 0), 0)
                  .toLocaleString("pt-BR", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
              </strong>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
